import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import * as z from 'zod';

import { usePatientUser } from 'src/shared/hooks/usePatientUser';

import { FormGroup } from '../shared/components/FormGroup';
import { Loader } from '../shared/components/Loader';

import parentStyles from './RecoverPasswordPage.module.scss';
import styles from './StartPasswordRecoveryForm.module.scss';

const schema = z.object({
  email: z.string().min(1),
});

type FormValues = z.infer<typeof schema>;

export function StartPasswordRecoveryForm() {
  const navigate = useNavigate();

  const { startPasswordRecovery } = usePatientUser(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = useCallback(
    async ({ email }: FormValues) => {
      setIsProcessing(true);
      try {
        const { username, unverifiedEmail } = await startPasswordRecovery(email);

        setIsProcessing(false);
        if (unverifiedEmail) {
          navigate(`success?unverified=true`);
        } else {
          navigate(`update-password?username=${username}`);
        }
      } catch (error) {
        //FIXME: show a toast message with the error
        setError('Unable to start password recovery. Please check the email provided.');
        setIsProcessing(false);
        captureException(error);
      }
    },
    [navigate, startPasswordRecovery]
  );

  return (
    <div className={styles.container}>
      <h1 className={parentStyles.title}>
        <FormattedMessage id="recover-password-page.title" defaultMessage="Recover your password" />
      </h1>
      {error && <div className={styles.error}>{error}</div>}
      <form className={styles.form} noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormGroup error={errors['email']}>
          <label htmlFor="userEmail">
            <FormattedMessage
              id="create-password-page.email-input-label"
              defaultMessage="Your email address"
            />
          </label>
          <input id="userEmail" type="email" {...register('email')} />
        </FormGroup>

        <div className={styles.actions}>
          <button type="submit" disabled={isProcessing}>
            <FormattedMessage id="create-password-page.form-submit-btn" defaultMessage="Continue" />
          </button>
        </div>
      </form>
      {isProcessing && <Loader fullscreen />}
    </div>
  );
}
